import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";

import AddIcon from "@material-ui/icons/AddOutlined";

import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Code";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import firebase from "firebase/app";
import _ from "lodash";
import { highlight, languages } from "prismjs";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useLocation, useParams } from "react-router-dom";
import Editor from "react-simple-code-editor";
import useInterval from "react-use/lib/useInterval";
import useLocalStorage from "react-use/lib/useLocalStorage";
import { v4 as uuidv4 } from "uuid";

import AnimateHeight from "react-animate-height";

import { useHistory } from "react-router";

import { useDispatch, useSelector } from "react-redux";

import "react-datetime/css/react-datetime.css";
import { DownloadIcon } from "./components/icons/themes/materialdesign";

import AutocompletePropField from "./AutocompletePropField";

import { fetchEventDefs } from "./actions/eventDefs";
import { downloadEventSchema } from "./components/EventCSVDownloadButton";
import { RefreshIcon } from "./components/icons/themes/windows10";
import {
  destinationKeyToSlugMap,
  warehouseDestinations,
} from "./destinations/destinations";
import {
  allEventTypes,
  eventCategories,
  eventDefType,
  eventSourceForEventType,
  isCustomEventType,
  libraryForEventType,
} from "./eventTypes";
import FilterList from "./FilterList";
import onEnvironmentChange from "./hooks/on-environment-change-by-user";
import useCurrentEnvironment from "./hooks/use-environment";
import { useAllSaasDestinations } from "./hooks/useAllSaasDestinations";
import { useDestinationMetadataManager } from "./hooks/useDestinationMetadataManager";
import Navigation from "./Navigation";
import { LOAD_EVENTS } from "./reducers/events";
import "./theme/custom.css";
import {
  getFeatureFlag,
  iconForEventType,
  isNetworkFilter,
  makeRequest,
  userIsFPEmployee,
} from "./util";

import { CodeEditorModal } from "./CodeEditorModal";
import eventsEmptyStateImage from "./img/events_empty_state.svg";
import useArrayOfDestinations from "./hooks/useArrayOfDestinations";
import useFeatureFlags from "./hooks/useFeatureFlag";
import EventDestinations from "./EventsPage/EventDestinations";

const EVENTS_PAGE_BASE = "/events/schema";

const useTopbarStyles = makeStyles((theme) => ({
  topbar: {
    display: "flex",
    backgroundColor: "transparent",
    width: "100%",
    flex: "0 1 auto",
    height: 70,
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  searchWrapper: {
    width: 275,
    borderBottom: "2px solid rgb(63 63 68 / 15%)",
  },
  searchField: {},
  exportEventButton: {
    position: "absolute",
    right: 0,
    top: 40,
    width: 180,
    height: 45,
    backgroundColor: "white",
    color: "#3f51b5",
    border: `1px solid ${alpha("#3f51b5", 0.5)}`,
    "&:hover": {
      backgroundColor: "#f5f5f5",
      border: `1px solid #3f51b5`,
    },
  },
  exportEventButtonWrapper: {
    position: "relative",
  },
}));

let EventsTopbar = ({
  searchString,
  onSearchStringChange,
  onNewEventDef,
  editorState,
  downloadEventSchema,
}) => {
  const classes = useTopbarStyles();

  const matchingCategory = _.find(
    eventCategories,
    (cat) => cat.type === editorState.type
  );

  const headline = matchingCategory
    ? `${matchingCategory.name} / ${editorState.name}`
    : editorState.name;

  return (
    <div className={clsx(classes.topbar)}>
      <div
        className={clsx(
          "bg-white p-4 flex justify-center items-center MuiPaper-elevation1",
          classes.searchWrapper
        )}
      >
        <TextField
          className="w-full"
          variant="outlined"
          name="search"
          autoComplete="off"
          onChange={(e) => onSearchStringChange(e.target.value)}
          value={searchString}
          InputProps={{
            className: clsx(classes.searchField),
            placeholder: "Search events",
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={clsx(classes.icon)} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div
        className={clsx(
          "px-5 py-4 items-center truncate font-bold text-gray-700 flex-grow"
        )}
      >
        <span className="text-2xl">{headline}</span>
      </div>

      <div className={clsx("p-3 flex justify-center items-center")}>
        <Button
          className="h-full"
          color="primary"
          onClick={() => onNewEventDef()}
          variant="contained"
        >
          <AddIcon className={classes.icon} />
          Create New Event
        </Button>
        <div className={clsx(classes.exportEventButtonWrapper)}>
          <Button
            className={clsx(classes.exportEventButton)}
            color="primary"
            onClick={() => downloadEventSchema()}
            variant="contained"
          >
            <DownloadIcon className={classes.icon} />
            Export Events
          </Button>
        </div>
      </div>
    </div>
  );
};

EventsTopbar.propTypes = {
  searchString: PropTypes.string,
  onSearchStringChange: PropTypes.func,
  onNewEventDef: PropTypes.func,
  editorState: PropTypes.any,
};
EventsTopbar = React.memo(EventsTopbar);

const useStyles = makeStyles(() => ({
  content: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  widget: {},
  widgets: {
    overflowY: "auto",
    paddingTop: 1,
    margin: 0,
  },
}));

// Trim semicolons and whitespace from end of SQL query
function trimSQLQuery(query) {
  return query.replace(/[\s;]+$/, "");
}

function getUserName() {
  const currentUser = firebase.auth().currentUser;
  if (currentUser === null) {
    return null;
  }

  return currentUser.displayName || currentUser.email;
}

export function EventsDrawer({ event, onClose }) {}

function EventsPage() {
  const classes = useStyles();
  const alert = useAlert();
  const location = useLocation();
  const urlParams = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const flag = useFeatureFlags();

  const [searchString, setSearchString] = useState("");

  const [enabledDests, setEnabledDests] = useState(null);
  const [disabledBackfills, setDisabledBackfills] = useState(null);
  const [eventEditorState, setEventEditorState] = useState({});
  const [unsavedEdits, setUnsavedEdits] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false);
  const [
    destinationSettingsModalOpen,
    setDestinationSettingsModalOpen,
  ] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  const destinations = useArrayOfDestinations();

  const { eventDefs, loading: eventDefsLoading } = useSelector(
    (state) => state.eventDefs
  );
  const events = useMemo(
    () => _.sortBy(eventDefs, (eventDef) => eventDef.name.toLowerCase()),
    [eventDefs]
  );
  const setEvents = useCallback(
    (data) => dispatch({ type: LOAD_EVENTS, payload: data }),
    [dispatch]
  );

  // KLUDGE: When changing the event definition settings, we want to immediately
  // change the event definition state, then trigger a save. We can't call
  // setState then make a request because the setState wouldn't have propagated
  // yet. Instead, we call setTriggerSaveEvent which will save the event the
  // next time through.
  const [triggerSaveEvent, setTriggerSaveEvent] = useState(false);

  // This can be either 'create' or 'edit' depending on whether we are creating
  // a new event or editing an old one.
  const [editorType, setEditorType] = useState(null);

  const handleClickNewEventDef = useCallback(() => {
    if (unsavedEdits) {
      alert.error("You have unsaved event definition changes.");
      return;
    }

    history.replace(`${EVENTS_PAGE_BASE}/new`);
  }, [alert, history, unsavedEdits]);

  const handleNewEventDefinition = useCallback(
    (type = "click", typeSpecificProperty = "", filters = []) => {
      setEnabledDests({});
      setDisabledBackfills({});
      setUnsavedEdits(true);
      setEventEditorState({
        id: uuidv4(),
        name: "",
        typeSpecificFilterVal: typeSpecificProperty,
        type: type,
        filters: filters,
        snapshots: [],
        segmentCompatible: false,
      });
      setEditorType("create");
      setErrorMessage("");
      setScreenshot("");
    },
    []
  );

  const handleClickEvent = useCallback(
    ({ id }) => {
      history.push(`${EVENTS_PAGE_BASE}/${id}`);
    },
    [history]
  );

  // Select the given event from the event list.
  const handleSelectEvent = useCallback((event) => {
    function convertStorageToUIFilters(type, stFilters) {
      if (type === "network") {
        let uiFilters = [];
        for (let stFilter of stFilters) {
          let res = convertStoragePropToUIPropAndElPath(stFilter);
          let uiFilter = {
            type: stFilter.type,
            prop: res[0],
            nw_path: res[1],
            op: stFilter.op,
            val: stFilter.val,
          };
          uiFilters.push(uiFilter);
        }
        return uiFilters;
      } else {
        return stFilters;
      }
    }

    function convertStoragePropToUIPropAndElPath(stFilter) {
      if (isNetworkFilter(stFilter)) {
        return ["$network", stFilter.prop];
      } else {
        return [stFilter.prop, ""];
      }
    }

    const eventType = eventDefType(event);
    var filters = event.filter.filters;

    var typeSpecificFilter;
    if (eventType === "pageview") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$pathname"
      );
    } else if (eventType === "network") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$pathname"
      );
    } else if (eventType === "react_navigation_screenview") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$screen_path"
      );
    } else if (
      eventType === "scheduled_event" ||
      eventType === "scheduled_identify"
    ) {
      filters = [];
      typeSpecificFilter = { val: event.sql_query };
    } else {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$elements"
      );
    }

    filters = convertStorageToUIFilters(
      eventType,
      filters.filter(
        (filter) =>
          filter.prop !== "$event_type" && filter !== typeSpecificFilter
      )
    );

    const editorState = {
      ...event,
      typeSpecificFilterVal: typeSpecificFilter && typeSpecificFilter.val,
      type: eventDefType(event),
      filters: filters,
      snapshots: event.snapshots || [],
      segmentCompatible: event.segment_compatible || false,
    };

    setEventEditorState(editorState);
    setUnsavedEdits(false);
    setEnabledDests(event.enabled_dests);
    setDisabledBackfills({});
    setEditorType("edit");
    setErrorMessage("");
    makeRequest("get_event_def_screenshot", { event_def_id: event.id }).then(
      (response) => {
        const screenshot = response.data;
        setScreenshot(screenshot);
      }
    );
  }, []);

  // Returns the type specific filter for the event current in the event def
  // editor.
  function editorStateTypeSpecificFilter() {
    if (eventEditorState.type === "pageview") {
      return {
        type: "prop_filter",
        prop: "$pathname",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else if (eventEditorState.type === "network") {
      return {
        type: "prop_filter",
        prop: "$pathname",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else if (eventEditorState.type === "react_navigation_screenview") {
      return {
        type: "prop_filter",
        prop: "$screen_path",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else {
      return {
        type: "prop_filter",
        prop: "$elements",
        op: "css_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    }
  }

  function convertUIPropAndElPathToStorageProp(uiFilter) {
    if (isNetworkFilter(uiFilter)) {
      return uiFilter.nw_path;
    } else {
      return uiFilter.prop;
    }
  }

  function convertUIToStorageFilters(type, uiFilters) {
    if (type === "network") {
      let stFilters = [];
      for (let uiFilter of uiFilters) {
        let stFilter = {
          type: uiFilter.type,
          prop: convertUIPropAndElPathToStorageProp(uiFilter),
          op: uiFilter.op,
          val: uiFilter.val,
        };
        stFilters.push(stFilter);
      }
      return stFilters;
    } else {
      return uiFilters;
    }
  }

  // Takes the current editor state and converts it into an event definition.
  function editorStateToEventDef() {
    var filters;

    if (isCustomEventType(eventEditorState.type)) {
      // For custom events, there isn't an $event_type filter or event type
      // specific filter so we just included the filters as is.
      filters = eventEditorState.filters;
    } else if (libraryForEventType(eventEditorState.type) === "sql") {
      filters = [
        {
          type: "prop_filter",
          prop: "$event_def_id",
          op: "=",
          val: eventEditorState.id,
        },
        {
          type: "prop_filter",
          prop: "mp_lib",
          op: "=",
          val: "sql",
        },
      ];
    } else {
      filters = [
        {
          type: "prop_filter",
          prop: "$event_type",
          op: "=",
          val: eventEditorState.type,
        },
        editorStateTypeSpecificFilter(),
      ].concat(
        convertUIToStorageFilters(
          eventEditorState.type,
          eventEditorState.filters
        )
      );
    }

    return {
      // by default, the event should have an empty tags object.
      // eventEditorState.tags will override this if present.
      tags: {},
      ...eventEditorState,
      filter: {
        type: "and",
        filters: filters,
      },
      source: eventSourceForEventType(eventEditorState.type),
      snapshots: eventEditorState.snapshots,
      segment_compatible: eventEditorState.segmentCompatible,
      description: eventEditorState.description,
      sql_query:
        (libraryForEventType(eventEditorState.type) === "sql" &&
          trimSQLQuery(eventEditorState.typeSpecificFilterVal)) ||
        null,
    };
  }

  // This updates the events array by replacing the event with the given id with
  // fn() applied to it.
  const updateEventDef = useCallback(
    function updateEventDef(eventDefID, fn) {
      const existingEventDef = events.find(
        (eventDef) => eventDef.id === eventDefID
      );
      const newEventDef = fn(existingEventDef);
      const eventDefs = events.filter((eventDef) => eventDef.id !== eventDefID);

      if (newEventDef) {
        var insertIndex = eventDefs.findIndex(
          (eventDef) =>
            newEventDef.name.toLowerCase() <= eventDef.name.toLowerCase()
        );

        if (insertIndex === -1) {
          insertIndex = eventDefs.length;
        }

        eventDefs.splice(insertIndex, 0, newEventDef);
      }

      setEvents(eventDefs);
    },
    [events, setEvents]
  );

  function cssSelectorIsValid(selector) {
    try {
      document.querySelectorAll(selector);
      return true;
    } catch {
      return false;
    }
  }

  function hasNetworkEventEditPerms() {
    return flag("networkEventDefs");
  }

  function isNetworkDynProp(snapshot) {
    return snapshot.type === "network";
  }

  function hasJsDynPropEditPerms() {
    return userIsFPEmployee() || flag("allowJsDynamicProp");
  }

  function isEventDefValid(eventDef) {
    if (eventDef.name.length === 0) {
      alert.error("The event definition has no name.");
      return false;
    }

    if (eventDef.filter && eventDef.filter.filters) {
      if (
        eventDef.filter.filters.find(
          (subFilter) =>
            subFilter.op === "css_matches" && !cssSelectorIsValid(subFilter.val)
        )
      ) {
        alert.error("The CSS selector you entered is not valid");
        return false;
      }
    }

    if (
      events.find(
        (otherEventDef) =>
          otherEventDef.name === eventDef.name &&
          otherEventDef.id !== eventDef.id &&
          otherEventDef.source == eventDef.source
      )
    ) {
      alert.error(
        "Unable to save event definition. The event definition has the same name as another event definition."
      );
      return false;
    }

    if (eventEditorState.typeSpecificFilterVal === "") {
      if (eventEditorState.type === "pageview") {
        alert.error("The event definition does not have a URL filter.");
      } else if (eventEditorState.type === "react_navigation_screenview") {
        alert.error("The event definition does not have a screen view filter.");
      } else if (eventEditorState.type === "network") {
        alert.error("The event definition does not have a URL filter.");
      } else if (libraryForEventType(eventEditorState.type) === "sql") {
        alert.error("The event definition does not have a SQL query.");
      } else {
        alert.error(
          "The event definition does not have a CSS selector filter."
        );
      }
      return false;
    }

    for (let filter of eventEditorState.filters) {
      if (!filter.prop) {
        alert.error("The property for one of the filters is unselected.");
        return false;
      }

      if (!filter.op) {
        alert.error("The operator for one of the filters is unselected.");
        return false;
      }

      if (isNetworkFilter(filter)) {
        if (eventEditorState.type !== "network") {
          alert.error(
            "There are one or more network-specific filters on a non-Network event definition."
          );
          return false;
        }
        if (!filter.nw_path || !filter.nw_path.startsWith(".")) {
          alert.error(
            "The NW Path for one or more of the network-specific filters does not start with a period."
          );
          return false;
        }
      }
    }

    for (let snapshot of eventEditorState.snapshots) {
      if (!(snapshot.name && snapshot.name !== "")) {
        alert.error(
          "One or more of the dynamic properties does not have a name."
        );
        return false;
      }
      if (!(snapshot.value && snapshot.value !== "")) {
        alert.error(
          "One or more of the dynamic properties does not have a value."
        );
        return false;
      }
      if (isNetworkDynProp(snapshot)) {
        if (eventEditorState.type !== "network") {
          alert.error(
            "There are one or more network-specific dynamic properties on a non-Network event definition."
          );
          return false;
        }
        if (!snapshot.value || !snapshot.value.startsWith(".")) {
          alert.error(
            "One or more network-specific dynamic properties does not have a value starting with a period."
          );
          return false;
        }
      }
    }

    return true;
  }

  // This saves the current event in the event def editor to the backend and
  // updates the local state.

  // handleSaveEvent is a dependency of a useEffect later on. This causes the
  // useEffect to rerun on every refresh. This is ok based on how the useEffect
  // works.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleSaveEvent() {
    const newEventDef = editorStateToEventDef();
    if (!isEventDefValid(newEventDef)) {
      return;
    }

    makeRequest("save_event_def", newEventDef)
      .then((resp) => {
        if (resp.data.error_message) {
          setErrorMessage(resp.data.error_message);
        } else {
          setErrorMessage("");
          const userName = getUserName();
          let newEventEntry = _.extend({}, newEventDef, {
            updated_at: new Date().toISOString(),
            last_updated_by: userName,
            enabled_dests: enabledDests,
          });
          if (editorType === "create") {
            newEventEntry = _.extend(newEventEntry, {
              created_at: new Date().toISOString(),
              created_by: userName,
            });
          }
          updateEventDef(newEventEntry.id, () => newEventEntry);

          setUnsavedEdits(false);
          setEditorType("edit");
          handleClickEvent({ id: newEventEntry.id });
          alert.success("Event Saved");
        }
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save event.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  const handleToggleDestination = useCallback(
    function handleToggleDestination(destID) {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      const newDestObj = {};
      const newState = !enabledDests[destID];
      newDestObj[destID] = newState;

      makeRequest("toggle_destination", {
        dest_id: destID,
        event_def_id: eventEditorState.id,
        state: newState,
      })
        .then((resp) => {
          if (resp.data.error_message) {
            setErrorMessage(resp.data.error_message);
          } else {
            setErrorMessage("");
            const newEnabledDests = _.extend({}, enabledDests, newDestObj);
            setEnabledDests(newEnabledDests);
            updateEventDef(eventEditorState.id, (eventDef) => {
              return _.extend({}, eventDef, { enabled_dests: newEnabledDests });
            });
          }
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to toggle destination.");
          console.log(error);
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [unsavedEdits, enabledDests, eventEditorState.id, alert, updateEventDef]
  );

  const sendBackfillRequest = useCallback(
    function sendBackfillRequest(destID, dateRange) {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      const defaultStart = new Date();
      defaultStart.setFullYear(defaultStart.getFullYear() - 5);

      const defaultEnd = new Date();
      defaultEnd.setHours(defaultEnd.getHours() + 24);

      const dateRangeWithDefaults = {
        start: dateRange.start || Math.round(defaultStart.getTime() / 1000),
        end: dateRange.end || Math.round(defaultEnd.getTime() / 1000),
      };

      setDisabledBackfills(_.extend({}, disabledBackfills, { [destID]: true }));

      makeRequest("backfill_event_def", {
        event_def_id: eventEditorState.id,
        dest_id: destID,
        datetime_bounds: dateRangeWithDefaults,
      })
        .then(() => alert.success("Backfill has begun, please wait"))
        .catch((error) => {
          alert.error("Something went wrong. Unable to start backfill.");
          console.log(error);
          setDisabledBackfills(
            _.extend({}, disabledBackfills, { [destID]: false })
          );
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, eventEditorState.id, unsavedEdits, disabledBackfills]
  );

  const handleEditorStateChange = useCallback(function handleEditorStateChange(
    fn
  ) {
    setUnsavedEdits(true);
    setEventEditorState(fn);
  },
  []);

  const handleCloseDeleteEventModal = useCallback(
    function handleCloseDeleteEventModal() {
      setDeleteEventModalOpen(false);
    },
    []
  );

  const handleOpenDestinationsSettingModal = useCallback(
    function handleOpenDestinationsSettingModal() {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      setDestinationSettingsModalOpen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, unsavedEdits]
  );

  const handleCloseDestinationSettingsModal = useCallback(
    function handleCloseDestinationSettingsModal() {
      setDestinationSettingsModalOpen(false);
    },
    []
  );

  const handleSaveDestinationSettingsModal = useCallback(
    function handleSaveDestinationSettingsModal(settings) {
      setEventEditorState((oldState) => {
        const newState = _.extend({}, oldState, settings);
        return newState;
      });

      setTriggerSaveEvent(true);
    },
    []
  );

  // After toggling segment compatibility we immediately save the event
  // definition.
  useEffect(
    function () {
      if (triggerSaveEvent) {
        handleSaveEvent();
        setTriggerSaveEvent(false);
      }
    },
    [triggerSaveEvent, handleSaveEvent]
  );

  // Handle URL param changes
  useEffect(() => {
    if (urlParams.eventDefID === "new") {
      // Creating one
      const queryParams = new URLSearchParams(location.search);
      var filters = [];

      if (!!queryParams.get("targetText")) {
        filters.push({
          type: "prop_filter",
          prop: "$el_nested_text",
          nw_path: "",
          op: "=",
          val: queryParams.get("targetText"),
        });
      }

      if (!!queryParams.get("path")) {
        filters.push({
          type: "prop_filter",
          prop: "$pathname",
          nw_path: "",
          op: "=",
          val: queryParams.get("path"),
        });
      }

      handleNewEventDefinition(
        queryParams.get("type") || undefined,
        queryParams.get("typeSpecificProperty") || undefined,
        filters
      );
    } else if (urlParams.eventDefID) {
      // Editing one
      const foundEvent = events.find((evt) => evt.id === urlParams.eventDefID);
      if (foundEvent) {
        handleSelectEvent(foundEvent);
      }
    } else {
      // Nothing.
      setEditorType(null);
      setEnabledDests(null);
      setDisabledBackfills(null);
      setEventEditorState({});
      setUnsavedEdits(false);
    }
  }, [
    urlParams,
    urlParams.eventDefID,
    handleNewEventDefinition,
    events,
    handleSelectEvent,
    location.search,
    location,
  ]);

  useEffect(() => {
    dispatch(fetchEventDefs());
  }, [dispatch]);

  onEnvironmentChange(() => {
    // When the environment changes, go back to the initial page
    history.replace(EVENTS_PAGE_BASE);
  });

  const setEventHidden = useCallback(
    function setEventHidden(isHidden) {
      makeRequest("set_event_def_hidden", {
        event_def_id: eventEditorState.id,
        is_hidden: isHidden,
      })
        .then(() => {
          updateEventDef(eventEditorState.id, (eventDef) => {
            return _.extend({}, eventDef, { is_hidden: isHidden });
          });
          alert.success(isHidden ? "Event Hidden" : "Event Restored");
        })
        .catch((error) => {
          alert.error(
            `Something went wrong. Unable to ${
              isHidden ? "hide" : "restore"
            } event.`
          );
          console.log(error);
          Sentry.captureException(error);
        });
    },
    [alert, eventEditorState.id, updateEventDef]
  );

  const handleDeleteEvent = useCallback(
    function handleDeleteEvent() {
      makeRequest("delete_event", { event_def_id: eventEditorState.id })
        .then(() => {
          setEditorType(null);
          setEnabledDests(null);
          setDisabledBackfills(null);
          setEventEditorState({});
          setUnsavedEdits(false);
          updateEventDef(eventEditorState.id, () => null);
          alert.success("Event Deleted");
          history.replace(EVENTS_PAGE_BASE);
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to delete event.");
          console.log(error);
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, eventEditorState.id, history, updateEventDef]
  );

  const isCustomEvent = isCustomEventType(eventEditorState.type);
  return (
    <>
      {!eventDefsLoading && (
        <>
          <EventsTopbar
            searchString={searchString}
            onSearchStringChange={setSearchString}
            onNewEventDef={handleClickNewEventDef}
            downloadEventSchema={() =>
              downloadEventSchema(events, destinations)
            }
            editorState={eventEditorState}
            unsavedEdits={unsavedEdits}
          />
          <div className={classes.content}>
            <EventList
              activeEvent={eventEditorState.id}
              events={events}
              searchString={searchString}
              onSelect={handleClickEvent}
            />
            <Grid
              container
              spacing={0}
              alignItems="flex-start"
              alignContent="flex-start"
              className={clsx(classes.widgets, "px-5 pb-2")}
            >
              {destinations && editorType && (
                <>
                  <Grid item xs={8}>
                    <div className={classes.widget}>
                      {isCustomEvent ? (
                        <CustomEventDisplay
                          editorState={eventEditorState}
                          setEventHidden={setEventHidden}
                          setEditorState={handleEditorStateChange}
                          onSave={handleSaveEvent}
                        />
                      ) : (
                        <EventEditor
                          key={eventEditorState.id}
                          editorState={eventEditorState}
                          editorType={editorType}
                          errorMessage={errorMessage}
                          setErrorMessage={setErrorMessage}
                          unsavedEdits={unsavedEdits}
                          setState={handleEditorStateChange}
                          onSave={handleSaveEvent}
                          onDelete={() => setDeleteEventModalOpen(true)}
                          screenshot={screenshot}
                          setScreenshot={setScreenshot}
                          hasNetworkEventEditPerms={hasNetworkEventEditPerms}
                          isNetworkDynProp={isNetworkDynProp}
                          hasJsDynPropEditPerms={hasJsDynPropEditPerms}
                          destinations={destinations}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={8}>
                    <div className={classes.widget}>
                      <EventDestinations
                        eventType={eventEditorState.type}
                        eventDestinationId={eventEditorState.id}
                        eventIsCustom={isCustomEvent}
                        destinations={destinations}
                        enabledDests={enabledDests}
                        disabledBackfills={disabledBackfills}
                        handleBackfill={sendBackfillRequest}
                        onToggleDestination={handleToggleDestination}
                        openDestinationsSettingModal={
                          handleOpenDestinationsSettingModal
                        }
                        segmentCompatible={
                          eventEditorState && eventEditorState.segmentCompatible
                        }
                      />
                    </div>
                  </Grid>
                </>
              )}
              {editorType === null && events && events.length === 0 && (
                <Grid item>
                  <EmptyState
                    onNewEventDef={() => handleNewEventDefinition()}
                  />
                </Grid>
              )}
            </Grid>
          </div>
        </>
      )}
      <DeleteEventModal
        open={deleteEventModalOpen}
        onClose={handleCloseDeleteEventModal}
        onConfirm={handleDeleteEvent}
      />
      <DestinationSettingsModal
        open={destinationSettingsModalOpen}
        onClose={handleCloseDestinationSettingsModal}
        onSave={handleSaveDestinationSettingsModal}
        settings={{
          segmentCompatible:
            eventEditorState && eventEditorState.segmentCompatible,
        }}
      />
    </>
  );
}

const useEventListStyles = makeStyles((theme) => ({
  eventListPaper: {
    height: "100%",
    width: 275,
    overflowY: "auto",
    flexShrink: 0,
    borderRadius: 0,
  },
  eventList: {
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  eventTypeHeader: {
    backgroundColor: "white",
    color: "#617984",
  },
  headerText: {
    "& span": {
      color: "#617984",
      fontWeight: "bold",
      textTransform: "uppercase",
      fontSize: 12,
    },
  },
  triangle: {
    borderTop: "4px solid #4050B5",
    borderLeft: "4px solid transparent",
    borderRight: "4px solid transparent",
    width: 0,
    height: 0,
    transition: ".5s all",
  },
  triangleOpen: {
    transform: "rotate(180deg)",
  },
  listItem: {
    borderBottom: "1px solid #eeeeee",
    overflow: "hidden",
    transition: "flex .5s ease-in-out",
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
    fill: "#617984",
  },
  active: {
    backgroundColor: "#C5CAE9",
    fontWeight: 550,
    "&:hover": {
      backgroundColor: "#C5CAE9",
    },
  },
  hidden: {
    visibility: "hidden",
  },
}));

// Renders the list of events. We do the string searching inside of EventList so
// React.memo will work. If the events object changes every call, React.memo
// will rerender this component.
const EventList = React.memo(function EventList({
  activeEvent,
  events,
  onSelect,
  searchString,
}) {
  const classes = useEventListStyles();

  events =
    events &&
    events.filter((event) =>
      event.name.toLowerCase().includes(searchString.toLowerCase())
    );

  const invisibleEvents = events
    ? events.filter((event) => !allEventTypes.includes(eventDefType(event)))
    : [];
  for (var i = 0; i < invisibleEvents.length; i++) {
    Sentry.captureException(
      new Error(`Event ${invisibleEvents[i].id} not showing in UI`)
    );
  }

  const hiddenCategory = { name: "hidden", type: "hidden" };
  const renderedCategories = eventCategories
    .concat([hiddenCategory])
    .map((category) => {
      return (
        <EventListCategory
          key={category.name}
          name={category.name}
          events={events}
          type={category.type}
          activeEvent={activeEvent}
          onSelect={onSelect}
        />
      );
    });

  return (
    <Paper
      className={clsx(
        classes.eventListPaper,
        (!events || events.length === 0) && classes.hidden
      )}
    >
      <List disablePadding className={clsx(classes.eventList)}>
        {renderedCategories}
      </List>
    </Paper>
  );
});

EventList.propTypes = {
  activeEvent: PropTypes.string,
  events: PropTypes.array,
  onSelect: PropTypes.func,
  searchString: PropTypes.string,
};

// EventListCategory renders an event list for one category of event.
function EventListCategory({ name, events, type, activeEvent, onSelect }) {
  const classes = useEventListStyles();
  const Icon = iconForEventType(type);

  const [isOpen, setIsOpen] = useLocalStorage(
    `events/shema/category/${type}/`,
    true
  );

  events =
    events &&
    events.filter((event) =>
      event.is_hidden ? type === "hidden" : eventDefType(event) === type
    );

  if (_.isEmpty(events)) {
    return null;
  }

  return (
    <>
      <ListItem
        divider
        className={clsx(classes.eventTypeHeader, "cursor-pointer")}
        onClick={() => setIsOpen(!isOpen)}
      >
        {Icon && <Icon height={20} width={20} className={classes.icon} />}
        <ListItemText
          classes={{ root: clsx(classes.headerText) }}
          primary={name}
        />
        <div
          className={clsx(classes.triangle, isOpen && classes.triangleOpen)}
        />
      </ListItem>
      <AnimateHeight duration={500} height={isOpen ? "auto" : 0}>
        {events.map((event) => {
          return (
            <ListItem
              className={clsx(
                classes.listItem,
                activeEvent === event.id && classes.active
              )}
              key={event.id}
              button
              onClick={() => onSelect(event)}
            >
              <ListItemText
                primary={event.name}
                className="truncate"
                title={event.name}
              />
            </ListItem>
          );
        })}
      </AnimateHeight>
    </>
  );
}

EventListCategory.propTypes = {
  name: PropTypes.string.isRequired,
  events: PropTypes.array,
  type: PropTypes.string.isRequired,
  activeEvent: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

const useEventEditorStyles = makeStyles((theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  filterRegion: {
    display: "flex",
    justifyContent: "flex-end",
    alignContent: "center",
  },
  createEventButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
    "&:disabled": {
      visibility: "hidden",
      cursor: "not-allowed",
    },
  },
  deleteEventButton: {
    color: colors.red[600],
    border: `1px solid ${alpha(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: alpha(
        colors.red[600],
        theme.palette.action.hoverOpacity
      ),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  eventActions: {
    display: "flex",
    justifyContent: "space-between",
  },
  hidden: {
    visibility: "hidden",
  },
}));

function shallowEqual(a, b) {
  return _.isEqualWith(a, b, (x, y) => {
    if (!_.isObject(x) && !_.isObject(y)) {
      return x === y;
    }

    return undefined;
  });
}

export const MemoTextField = React.memo(TextField, (a, b) => {
  return shallowEqual(_.omit(a, "children"), _.omit(b, "children"));
});

export function CustomEventDisplay({
  editorState,
  setEventHidden,
  setEditorState,
  onSave,
}) {
  const [hasChanges, setHasChanges] = useState(false);
  const classes = useEventEditorStyles();

  const handlePropertyChange = useCallback(
    function handlePropertyChange(prop, value) {
      setEditorState((editorState) => {
        const newEditorState = _.extend({}, editorState, { [prop]: value });
        return newEditorState;
      });
    },
    [setEditorState]
  );

  const setDescription = useCallback(
    function setName(e) {
      if (!hasChanges) {
        setHasChanges(true);
      }

      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange, hasChanges]
  );

  return (
    <Card>
      <CardHeader
        title="Event Name"
        action={
          <div aria-label="info" className="text-xs text-grey-600">
            {editorState.created_at && (
              <>
                <span className="font-bold mr-3">Created:</span>
                {new Date(editorState.created_at).toLocaleString()}
                {editorState.created_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.created_by}
                  </>
                )}
                <br />
              </>
            )}
            {editorState.updated_at && (
              <>
                <span className="font-bold mr-2">Updated:</span>
                {new Date(editorState.updated_at).toLocaleString()}
                {editorState.last_updated_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.last_updated_by}
                  </>
                )}
              </>
            )}
          </div>
        }
      />
      <Divider />
      <CardContent>
        <div style={{ paddingLeft: "15px" }}>
          <Typography variant="h5">{editorState.name}</Typography>
        </div>
      </CardContent>
      <Divider />
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this event"
              name="description"
              onChange={setDescription}
              value={editorState.description}
            />
          </Grid>
        </Grid>

        <Grid container style={{ marginTop: 16 }}>
          <Button
            className={clsx(classes.deleteEventButton)}
            onClick={() => setEventHidden(!editorState.is_hidden)}
          >
            {editorState.is_hidden ? "Restore Event" : "Hide Event"}
          </Button>
          <Button
            style={{ marginLeft: "auto" }}
            className={classes.createEventButton}
            onClick={onSave}
            variant="outlined"
            disabled={!hasChanges}
          >
            Save
          </Button>
        </Grid>
      </CardContent>
    </Card>
  );
}

CustomEventDisplay.propTypes = {
  editorState: PropTypes.object.isRequired,
  setEventHidden: PropTypes.func.isRequired,
  setEditorState: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// This is there is only a single instance of this object. This allows us to use
// memoization for the MemoTextField when passing in the shrink argument.
const shrink = { shrink: true };
const native = { native: true };

export function EventEditor({
  screenshot,
  editorState,
  editorType,
  unsavedEdits,
  errorMessage,
  setErrorMessage,
  setState,
  setScreenshot,
  onSave,
  onDelete,
  hasNetworkEventEditPerms,
  isNetworkDynProp,
  hasJsDynPropEditPerms,
  destinations,
}) {
  const alert = useAlert();
  const classes = useEventEditorStyles();
  const flag = useFeatureFlags();
  const currentEnvironment = useCurrentEnvironment();
  const [changeNwEventModalOpen, setChangeNwEventModalOpen] = useState(false);
  const [changeNwEventNewType, setChangeNwEventNewType] = useState({});
  const allSaasDestinations = useAllSaasDestinations();
  const allDestinations = { ...warehouseDestinations, ...allSaasDestinations };
  const destinationMetadataManager = useDestinationMetadataManager();
  const library = libraryForEventType(editorState.type);
  const validGADestinations = destinations.filter((dest) => {
    const destinationSlug =
      destinationKeyToSlugMap[dest.name] || dest.function_id;
    const destinationDefinition = allDestinations[destinationSlug];
    return (
      dest.enabled &&
      dest.name.startsWith("Google Analytics") &&
      destinationMetadataManager.destinationIsValidForEvent(
        dest,
        editorState.type
      ) &&
      (!destinationDefinition.supportedSources ||
        destinationDefinition.supportedSources[library])
    );
  });
  let nameHelperText = "";
  if (editorState.name.length > 40 && validGADestinations.length > 0) {
    nameHelperText =
      "Note: Google Analytics will truncate the Event Name at 40 characters";
  }

  const handlePropertyChange = useCallback(
    function handlePropertyChange(prop, value) {
      setState((editorState) => {
        const newEditorState = _.extend({}, editorState, { [prop]: value });
        return newEditorState;
      });
    },
    [setState]
  );

  const setName = useCallback(
    function setName(e) {
      handlePropertyChange("name", e.target.value);
    },
    [handlePropertyChange]
  );

  const setDescription = useCallback(
    function setName(e) {
      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange]
  );

  const setType = useCallback(
    function setType(e) {
      setErrorMessage("");
      setChangeNwEventNewType({});
      if (editorState.type === "network" && e.target.value !== "network") {
        let confirmNeeded = false;
        for (let uiFilter of editorState.filters) {
          if (isNetworkFilter(uiFilter)) {
            confirmNeeded = true;
          }
        }
        for (let snapshot of editorState.snapshots) {
          if (isNetworkDynProp(snapshot)) {
            confirmNeeded = true;
          }
        }
        if (confirmNeeded) {
          changeNwEventNewType.type = e.target.value;
          setChangeNwEventModalOpen(true);
          return;
        }
      }
      handlePropertyChange("type", e.target.value);
    },
    [
      setErrorMessage,
      handlePropertyChange,
      changeNwEventNewType.type,
      editorState.filters,
      editorState.snapshots,
      editorState.type,
      isNetworkDynProp,
    ]
  );

  const handleChangeNwEvent = useCallback(
    function handleChangeNetworkEvent() {
      //Delete network-specific filters and dyn props

      setState((editorState) => {
        let newFilters = [];
        for (let uiFilter of editorState.filters) {
          if (!isNetworkFilter(uiFilter)) {
            newFilters.push(uiFilter);
          }
        }
        let newSnapshots = [];
        for (let snapshot of editorState.snapshots) {
          if (!isNetworkDynProp(snapshot)) {
            newSnapshots.push(snapshot);
          }
        }
        const newState = _.extend({}, editorState, {
          type: changeNwEventNewType.type,
          filters: newFilters,
          snapshots: newSnapshots,
        });

        return newState;
      });
    },
    [changeNwEventNewType.type, setState, isNetworkDynProp]
  );

  function handleChangeNwEventModalCancel() {
    setChangeNwEventModalOpen(false);
  }

  const setTypeSpecificFilterVal = useCallback(
    function setTypeSpecificFilterVal(value) {
      handlePropertyChange("typeSpecificFilterVal", value);
    },
    [handlePropertyChange]
  );

  const setLibrary = useCallback(
    function setLibrary(e) {
      // We determine the library in use based on the event type. When you toggle
      // a new library, we just change the current event type to one from the
      // designated library.
      setErrorMessage("");
      if (e.target.value === "web") {
        handlePropertyChange("type", "click");
      } else if (e.target.value === "react-native") {
        handlePropertyChange("type", "touch");
      } else if (e.target.value === "sql") {
        handlePropertyChange("type", "scheduled_event");
      }
    },
    [setErrorMessage, handlePropertyChange]
  );

  function handleAddFilter() {
    setState((editorState) => {
      const newFilter = {
        type: "prop_filter",
        prop: "",
        nw_path: "",
        op: "",
        val: "",
      };

      const newFilters = editorState.filters.concat([newFilter]);
      const newState = _.extend({}, editorState, { filters: newFilters });
      return newState;
    });
  }

  function handleAddSnapshot() {
    setState((editorState) => {
      const newSnapshot = {
        name: "",
        type: "css_selector",
        value: "",
      };

      const newSnapshots = editorState.snapshots.concat([newSnapshot]);
      const newState = _.extend({}, editorState, { snapshots: newSnapshots });
      return newState;
    });
  }

  // optionsForLibrary returns the list of option components for the event type
  // dropdown.
  function optionsForLibrary(library) {
    if (library === "web") {
      return (
        <>
          <option value="click">Click</option>
          <option value="pageview">Pageview</option>
          <option value="submit">Form Submit</option>
          <option value="change">Change</option>
          {hasNetworkEventEditPerms() && (
            <>
              <option value="network">Network</option>
            </>
          )}
        </>
      );
    } else if (library === "react-native") {
      return (
        <>
          <option value="touch">Touch</option>
          <option value="text_edit">Edit Input</option>
          <option value="toggle">Toggle Switch</option>
          <option value="react_navigation_screenview">Screen View</option>
        </>
      );
    } else if (library === "sql") {
      return (
        <>
          <option value="scheduled_event">Scheduled Event</option>;
          <option value="scheduled_identify">Scheduled Identify</option>;
        </>
      );
    } else {
      return <></>;
    }
  }

  const setFilterListState = useCallback(
    function setFilterListState(updateFiltersFn) {
      setState((editorState) => {
        const newFilters = updateFiltersFn(editorState.filters);
        const newState = _.extend({}, editorState, { filters: newFilters });
        return newState;
      });
    },
    [setState]
  );

  const setSnapshotListState = useCallback(
    function setSnapshotListState(updateSnapshotsFn) {
      setState((editorState) => {
        const newSnapshots = updateSnapshotsFn(editorState.snapshots);
        const newState = _.extend({}, editorState, { snapshots: newSnapshots });
        return newState;
      });
    },
    [setState]
  );

  var typeSpecificFilterName;
  var typeSpecificFilterProp;
  var placeholderText;

  if (editorState.type === "pageview") {
    typeSpecificFilterName = "URL Path";
    typeSpecificFilterProp = "$pathname";
    placeholderText = "For example /about or /products/*";
  } else if (editorState.type === "react_navigation_screenview") {
    typeSpecificFilterName = "Screen View Route";
    typeSpecificFilterProp = "$screen_path";
  } else if (editorState.type === "network") {
    typeSpecificFilterName = "URL Path";
    typeSpecificFilterProp = "$pathname";
    placeholderText = "For example /item or /item/*";
  } else {
    typeSpecificFilterName = "CSS Selector";
    typeSpecificFilterProp = null;
  }

  const proteusEnabled =
    !currentEnvironment.loaded ||
    !!getFeatureFlag(currentEnvironment.project, "sqlTransformations");

  useEffect(() => {
    if (libraryForEventType(editorState.type) === "sql" && !proteusEnabled) {
      setErrorMessage(
        `Contact support@freshpaint.io to enable this feature for the ${currentEnvironment.project.name} project. Provide your project ID: ${currentEnvironment.project.ID}.`
      );
    }
  }, [editorState.type, setErrorMessage, currentEnvironment, proteusEnabled]);

  const [isSyncing, setSyncing] = useState(false);
  // syncStatus is the latest result of the batch event definition sync. This is
  // refreshed regularly by polling.
  const [syncStatus, setSyncStatus] = useState();

  // Lazily grab the initial sync status
  useEffect(() => {
    if (
      editorType === "create" ||
      libraryForEventType(editorState.type) !== "sql" ||
      !proteusEnabled
    ) {
      return;
    }
    if (syncStatus) {
      return;
    }
    makeRequest("get_batch_event_def_sync_status", {
      event_def_id: editorState.id,
    }).then((syncStatusResp) => {
      setSyncing(syncStatusResp.data.lock_value != "");
      setSyncStatus((status) => {
        if (status) {
          return status;
        }
        return syncStatusResp.data;
      });
    });
  });

  // Poll every 10 seconds to update the sync status
  useInterval(() => {
    if (
      editorType === "create" ||
      libraryForEventType(editorState.type) !== "sql" ||
      !proteusEnabled
    ) {
      return;
    }
    makeRequest("get_batch_event_def_sync_status", {
      event_def_id: editorState.id,
    }).then((syncStatusResp) => {
      setSyncing(syncStatusResp.data.lock_value !== "");
      if (
        !syncStatus ||
        syncStatusResp.data.last_completed_at !== syncStatus.last_completed_at
      ) {
        setSyncStatus(syncStatusResp.data);
        alert.success(
          "Scheduled Event Definition Sync completed: " +
            getSyncStatusString(syncStatusResp.data)
        );
      }
    });
  }, 10000);

  function onSyncNow() {
    if (unsavedEdits) {
      alert.error("You have unsaved event definition changes.");
      return;
    }

    setSyncing(true);
    makeRequest("sync_batch_event_def", { event_def_id: editorState.id })
      .then((syncResponse) => {
        if (syncResponse.data.error_message) {
          // This tells the user if a sync was already in progress
          alert.success(syncResponse.data.error_message);
        } else {
          alert.success(
            "Scheduled Event Definition Sync initiated. This may take several minutes to complete."
          );
        }
      })
      .catch((error) => {
        setSyncing(false);
        alert.error("Something went wrong. Unable to sync event definition.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleDeleteScreenshot() {
    makeRequest("delete_event_def_screenshot", { event_def_id: editorState.id })
      .then((resp) => {
        setScreenshot("");
      })
      .catch((error) => {
        alert.error("Unable to remove screenshot");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  return (
    <Card>
      <CardHeader
        title={
          editorType === "create"
            ? "Create A New Event"
            : "Edit Event" + (unsavedEdits ? " (Unsaved Changes)" : "")
        }
        action={
          <div aria-label="info" className="text-xs text-grey-600">
            {editorState.created_at && (
              <>
                <span className="font-bold mr-3">Created:</span>
                {new Date(editorState.created_at).toLocaleString()}
                {editorState.created_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.created_by}
                  </>
                )}
                <br />
              </>
            )}
            {editorState.updated_at && (
              <>
                <span className="font-bold mr-2">Updated:</span>
                {new Date(editorState.updated_at).toLocaleString()}
                {editorState.last_updated_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.last_updated_by}
                  </>
                )}
              </>
            )}
          </div>
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              autoFocus={editorType === "create"}
              InputLabelProps={shrink}
              variant="outlined"
              label="Event Name"
              helperText={nameHelperText}
              name="name"
              onChange={setName}
              value={editorState.name}
            />
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Library"
              name="library"
              onChange={setLibrary}
              select
              SelectProps={native}
              value={libraryForEventType(editorState.type)}
              variant="outlined"
            >
              <option value="web">Web</option>
              <option value="react-native">React Native</option>
              {flag("sqlTransformations") && <option value="sql">SQL</option>}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Event Type"
              name="event-type"
              onChange={setType}
              select
              SelectProps={native}
              value={editorState.type}
              variant="outlined"
            >
              {optionsForLibrary(libraryForEventType(editorState.type))}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            {libraryForEventType(editorState.type) === "sql" ? (
              <Editor
                className="border-0 bg-gray-100 px-2 py-3 text-gray-900 mb-4 font-mono text-xs"
                padding={12}
                style={{ minHeight: 120 }}
                onValueChange={setTypeSpecificFilterVal}
                value={editorState.typeSpecificFilterVal || ""}
                placeholder="SELECT insert_id, user_id FROM events..."
                highlight={(code) => highlight(code, languages.sql)}
              />
            ) : (
              <AutocompletePropField
                propName={typeSpecificFilterProp}
                fullWidth
                autoComplete="off"
                variant="outlined"
                InputLabelProps={shrink}
                label={typeSpecificFilterName}
                placeholder={placeholderText}
                name="type-specific-filter-val"
                onChange={setTypeSpecificFilterVal}
                value={editorState.typeSpecificFilterVal}
              />
            )}
            {errorMessage && (
              <p className="text-xs text-red-600">{errorMessage}</p>
            )}
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this event"
              name="description"
              onChange={setDescription}
              value={editorState.description}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      {libraryForEventType(editorState.type) !== "sql" && (
        <>
          <CardContent>
            <FilterList
              filters={editorState.filters}
              setState={setFilterListState}
              library={libraryForEventType(editorState.type)}
              eventType={editorState.type}
            />
            <div className={classes.filterRegion}>
              <Button
                color="default"
                onClick={handleAddFilter}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Filter
              </Button>
            </div>
          </CardContent>
          <Divider />
          <CardContent>
            <SnapshotList
              eventType={editorState.type}
              snapshots={editorState.snapshots}
              setState={setSnapshotListState}
              isNetworkDynProp={isNetworkDynProp}
              hasNetworkEventEditPerms={hasNetworkEventEditPerms}
              hasJsDynPropEditPerms={hasJsDynPropEditPerms}
            />
            <div className={classes.filterRegion}>
              <Button
                color="default"
                onClick={handleAddSnapshot}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Dynamic Property
              </Button>
            </div>
          </CardContent>
        </>
      )}
      <Divider />
      <CardContent className={classes.eventActions}>
        <Button
          className={clsx(
            classes.deleteEventButton,
            editorType === "create" && classes.hidden
          )}
          onClick={onDelete}
        >
          Remove Event
        </Button>

        <Button
          className={classes.createEventButton}
          onClick={onSave}
          variant="outlined"
          disabled={
            libraryForEventType(editorState.type) === "sql" && !proteusEnabled
          }
        >
          {editorType === "create" ? "Create Event" : "Save"}
        </Button>
      </CardContent>
      <ChangeNetworkEventModal
        open={changeNwEventModalOpen}
        onClose={handleChangeNwEventModalCancel}
        onConfirm={handleChangeNwEvent}
      />
      {screenshot && (
        <>
          <Divider />
          <CardHeader
            title="Screenshot Preview"
            style={{ "padding-bottom": 0 }}
          />
          <CardContent>
            <img src={screenshot} />

            <Button
              className={clsx(classes.deleteEventButton)}
              onClick={handleDeleteScreenshot}
              style={{ marginTop: "15px" }}
            >
              Remove Screenshot
            </Button>
          </CardContent>
        </>
      )}
      {editorType !== "create" &&
        libraryForEventType(editorState.type) === "sql" && (
          <>
            <Divider />
            <CardContent className={classes.eventActions + " items-center"}>
              {syncStatus ? (
                <Typography variant="body1">
                  {getSyncStatusString(syncStatus)}
                </Typography>
              ) : (
                <div className="px-3 py-2 font-bold text-lg flex items-center rounded">
                  <RefreshIcon className="animate-spin mx-auto" />
                </div>
              )}
              {isSyncing && (
                <div className="px-3 py-2 font-bold text-lg flex items-center rounded">
                  <RefreshIcon className="animate-spin mx-auto" />
                </div>
              )}
              <Button
                color="primary"
                variant="outlined"
                onClick={onSyncNow}
                disabled={isSyncing || !proteusEnabled}
              >
                {isSyncing
                  ? "Sync In Progress"
                  : "Sync " + currentEnvironment.name + " Now"}
              </Button>
            </CardContent>
          </>
        )}
    </Card>
  );
}

EventEditor.propTypes = {
  editorState: PropTypes.object.isRequired,
  editorType: PropTypes.string.isRequired,
  unsavedEdits: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
  setScreenshot: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function SnapshotList({
  eventType,
  snapshots,
  setState,
  isNetworkDynProp,
  hasNetworkEventEditPerms,
  hasJsDynPropEditPerms,
}) {
  const handleSnapshotChange = useCallback(
    function handleSnapshotChange(index, snapshot) {
      setState((snapshots) => {
        const newList = snapshots.slice();
        newList[index] = snapshot;
        return newList;
      });
    },
    [setState]
  );

  const handleSnapshotDeletion = useCallback(
    function handleSnapshotDeletion(index) {
      setState((snapshots) => {
        const newSnapshots = snapshots.slice();
        newSnapshots.splice(index, 1);
        return newSnapshots;
      });
    },
    [setState]
  );

  if (typeof snapshots == "undefined") {
    snapshots = [];
  }
  const renderedSnapshots = snapshots.map((snapshot, index) => {
    return (
      <Snapshot
        eventType={eventType}
        key={index}
        index={index}
        snapshot={snapshot}
        onChange={handleSnapshotChange}
        onDelete={handleSnapshotDeletion}
        isNetworkDynProp={isNetworkDynProp}
        hasNetworkEventEditPerms={hasNetworkEventEditPerms}
        hasJsDynPropEditPerms={hasJsDynPropEditPerms}
      />
    );
  });

  return <>{renderedSnapshots}</>;
}

SnapshotList.propTypes = {
  snapshots: PropTypes.array.isRequired,
  setState: PropTypes.func.isRequired,
};

const snapshotStyles = makeStyles(() => ({
  snapshot: {
    marginBottom: 16,
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  bold: {
    fontWeight: 550,
  },
}));

const Snapshot = React.memo(function Snapshot({
  eventType,
  snapshot,
  index,
  onChange,
  onDelete,
  isNetworkDynProp,
  hasNetworkEventEditPerms,
  hasJsDynPropEditPerms,
}) {
  let codeEditorPrompt;
  if (snapshot.type === "js_code") {
    codeEditorPrompt = `Provide only a function body ending with a return statement.

This variable is available in your code:

  var event; // The DOM event, if available (may be null)

Example:

  if (event.altKey) {
    return 'SUPPRESS_EVENT';
  }
  return ('x:' + event.clientX + ',y:' + event.clientY + ')';

Returning 'SUPPRESS_EVENT', as shown above, suppresses the event from being
delivered to destinations.`;
  } else if (snapshot.type === "css_selector") {
    codeEditorPrompt = `Enter a CSS Selector.\n\n`;
  } else {
    codeEditorPrompt =
      `Enter a dot-notation path.\n` +
      `\n` +
      ` Examples:\n` +
      `   .$request_headers.x-reddit-session\n` +
      `   .$response.purch_info.amt\n` +
      `   .$query_string_params.region\n` +
      `\n`;
  }

  const classes = snapshotStyles();
  const alert = useAlert();

  const [codeEditorModalOpen, setCodeEditorModalOpen] = useState(false);

  function handleCodeEditorModalSave(value) {
    handlePropertyChange("value", value);
    setCodeEditorModalOpen(false);
  }

  function handleCodeEditorModalCancel() {
    setCodeEditorModalOpen(false);
  }

  function handlePropertyChange(prop, value) {
    const newSnapshot = _.extend({}, snapshot, { [prop]: value });
    if (prop === "type") {
      if (isNetworkDynProp(newSnapshot) && !hasNetworkEventEditPerms()) {
        alert.error(
          "You do not have permissions to create Network-specific dynamic properties."
        );
        return;
      }
      if (value === "js_code" && !hasJsDynPropEditPerms()) {
        alert.error(
          "You do not have permissions to create JavaScript dynamic properties."
        );
        return;
      }
    }
    onChange(index, newSnapshot);
  }

  // allowEdit - if editing allowed on current snapshot, based on user types, perms.
  function allowEdit(snapshot) {
    if (isNetworkDynProp(snapshot)) {
      return hasNetworkEventEditPerms();
    }
    if (snapshot.type === "js_code") {
      return hasJsDynPropEditPerms();
    }
    return true;
  }

  return (
    <>
      <Grid
        container
        spacing={1}
        className={classes.snapshot}
        id="event-snapshot-wrapper"
      >
        <Grid item xs={3}>
          <MemoTextField
            fullWidth
            autoComplete="off"
            label="Property Name"
            name="dynamic-property-name"
            onChange={(e) => handlePropertyChange("name", e.target.value)}
            InputLabelProps={{ shrink: true }}
            value={snapshot.name}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          />
        </Grid>
        <Grid item xs={3}>
          <MemoTextField
            fullWidth
            label="Type"
            name="type"
            onChange={(e) => handlePropertyChange("type", e.target.value)}
            select
            SelectProps={native}
            value={snapshot.type}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          >
            <option value="css_selector">CSS Selector</option>
            <option value="js_code">JavaScript</option>
            {eventType === "network" && (
              <>
                <option value="network">Network</option>
              </>
            )}
          </MemoTextField>
        </Grid>
        <Grid item xs style={{ position: "relative" }}>
          <AutocompletePropField
            propName={snapshot.name}
            fullWidth
            label="Value"
            name="value"
            onChange={(value) => handlePropertyChange("value", value)}
            value={snapshot.value}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          />
          <Box
            component="button"
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            <EditIcon onClick={() => setCodeEditorModalOpen(true)} />
          </Box>
        </Grid>

        <Grid className={clsx(classes.closeIcon)} item xs={1}>
          <CloseIcon onClick={() => onDelete(index)} />
        </Grid>
      </Grid>
      {codeEditorModalOpen && (
        <CodeEditorModal
          onCancel={handleCodeEditorModalCancel}
          onSave={handleCodeEditorModalSave}
          parentElementID="event-snapshot-wrapper"
          initialCode={snapshot.value}
          textPrompt={codeEditorPrompt}
          saveEnabled={allowEdit(snapshot)}
          languageGrammar={languages.js}
          language="JavaScript"
        />
      )}
    </>
  );
});

Snapshot.propTypes = {
  eventType: PropTypes.string.isRequired,
  snapshot: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isNetworkDynProp: PropTypes.func.isRequired,
  hasNetworkEventEditPerms: PropTypes.func.isRequired,
  hasJsDynPropEditPerms: PropTypes.func.isRequired,
};

export function getSyncStatusString(syncStatus) {
  if (!syncStatus.last_completed_at) {
    return "Awaiting Initial Sync";
  }

  const completedAt = new Date(syncStatus.last_completed_at).toLocaleString();
  if (syncStatus.last_error_message) {
    return (
      "Last sync failed at " +
      completedAt +
      ": " +
      syncStatus.last_error_message
    );
  }

  const numEvents = syncStatus.last_num_events;
  const resultString =
    numEvents + " event" + (numEvents === 1 ? "" : "s") + " synced";
  return "Last sync: " + resultString + " at " + completedAt;
}

const useEmptyStateClasses = makeStyles(() => ({
  container: {
    paddingTop: 150,
    paddingLeft: 180,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  paragraph: {
    width: 540,
    margin: 20,
  },
}));

// This shows what to render when no events have been created yet.
function EmptyState({ onNewEventDef }) {
  const classes = useEmptyStateClasses();

  return (
    <div className={clsx(classes.container)}>
      <img
        className="mb-4"
        alt="Man creating event schemas"
        src={eventsEmptyStateImage}
        height={201}
        width={273}
      />
      <Typography component="h1" gutterBottom variant="h3">
        You haven&apos;t created any events yet
      </Typography>
      <Typography component="p" className={classes.paragraph}>
        If you&apos;ve installed the Autotrack snippet, Freshpaint is already
        collecting every interaction. Create events in Freshpaint to describe
        certain behaviors on your site or app, and send autotracked data to your
        Destinations.
      </Typography>
      <Button color="primary" onClick={onNewEventDef} variant="contained">
        <AddIcon className={classes.icon} />
        Create New Event
      </Button>
    </div>
  );
}

EmptyState.propTypes = {
  onNewEventDef: PropTypes.func,
};

export const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflow: "visible",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  backfillButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.blue[600],
    "&:hover": {
      backgroundColor: colors.blue[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.blue[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

const ChangeNetworkEventModal = React.memo(function ChangeNetworkEventModal({
  open,
  onClose,
  onConfirm,
}) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Change from Network event"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            There are one or more Network-specific filters / dynamic properties
            which will be removed if you proceed.
            <br />
            <br />
            <span className={clsx(classes.bold)}>
              Are you sure you want to change from a Network event and lose
              these filters / dynamic properties?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>CANCEL</Button>
          <Button
            className={clsx(classes.deactivateButton)}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant="contained"
          >
            CONFIRM
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
});

ChangeNetworkEventModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export const DeleteEventModal = React.memo(function DeleteEventModal({
  open,
  onClose,
  onConfirm,
}) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Remove Event"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            This is going to affect any Destinations you are sending this event
            to, but will not delete any data from Freshpaint.
            <br />
            <br />
            <span className={clsx(classes.bold)}>
              Are you sure you want to remove this event?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>CANCEL</Button>
          <Button
            className={clsx(classes.deactivateButton)}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant="contained"
          >
            REMOVE EVENT
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
});

DeleteEventModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export const DestinationSettingsModal = React.memo(
  function DestinationSettingsModal({ open, onClose, onSave, settings }) {
    const classes = useModalStyles();

    const [settingsToUpdate, setSettingsToUpdate] = useState({});

    if (!open) {
      return null;
    }

    function addSetting(key, value) {
      const newSettingsToUpdate = _.extend({}, settingsToUpdate, {
        [key]: value,
      });
      setSettingsToUpdate(newSettingsToUpdate);
    }

    function lookupSetting(key) {
      if (key in settingsToUpdate) {
        return settingsToUpdate[key];
      }

      return settings[key];
    }

    function handleSave() {
      onClose();
      onSave(settingsToUpdate);
      setSettingsToUpdate({});
    }

    function handleClose() {
      onClose();
      setSettingsToUpdate({});
    }

    return (
      <Modal onClose={onClose} open={open}>
        <Card className={clsx(classes.root)}>
          <CardHeader
            title="Event Destinations: Advanced Settings"
            classes={{ action: clsx(classes.closeIconAction) }}
            action={<CloseIcon onClick={onClose} />}
          />
          <Divider />
          <CardContent>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={9}>
                <Typography variant="body1">
                  <span className={clsx(classes.bold)}>
                    Override hardcoded destinations?
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Switch
                  checked={!lookupSetting("segmentCompatible")}
                  color="secondary"
                  name="enable-destination-for-event"
                  onChange={() =>
                    addSetting(
                      "segmentCompatible",
                      !lookupSetting("segmentCompatible")
                    )
                  }
                  value={!lookupSetting("segmentCompatible")}
                />
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions className={classes.actions}>
            <Button onClick={() => handleClose()}>CLOSE</Button>

            <Button
              onClick={() => handleSave()}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </CardActions>
        </Card>
      </Modal>
    );
  }
);

DestinationSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
};

const navigationStyles = makeStyles(() => ({
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
    height: "100%",
    display: "flex",
    flexFlow: "column",
  },
}));

function EventsPageWithNavigation() {
  const classes = navigationStyles();

  return (
    <Navigation className={classes.navigation}>
      <EventsPage />
    </Navigation>
  );
}

export default EventsPageWithNavigation;
